import React, { Component } from 'react';
import StatusPill from './StatusPill';

const FIELDS = [
    { key: 'fact_find', label: 'Fact Find', greenVerb: 'Submitted' },
    { key: 'id_verification', label: 'ID Verification', greenVerb: 'Verified' },
    { key: 'documents', label: 'Documents', greenVerb: 'Submitted' },
    { key: 'bank_statements', label: 'Bank Statements', greenVerb: 'Received' },
    { key: 'privacy_consent', label: 'Privacy', greenVerb: 'Signed' }
];

const GREEN_STATUSES = ['complete', 'passed', 'valid', 'received'];
const AMBER_STATUSES = ['partial', 'expired'];
const RED_STATUSES = ['pending', 'incomplete', 'missing', 'flagged'];

// Days thresholds for follow-up escalation. Persisted per-organisation as
// `settings.tracking_follow_up_min_days` and `tracking_follow_up_max_days`.
//   < min  -> "Active"     (recent, leave alone)
//   min..max -> "Follow up" (chase the client)
//   >= max -> "Stalled"    (escalate / consider archiving)
export const DEFAULT_FOLLOW_UP_MIN_DAYS = 7;
export const DEFAULT_FOLLOW_UP_MAX_DAYS = 30;

function resolveRange(range) {
    let min = range && typeof range.minDays === 'number' && range.minDays > 0 ? range.minDays : DEFAULT_FOLLOW_UP_MIN_DAYS;
    let max = range && typeof range.maxDays === 'number' && range.maxDays > 0 ? range.maxDays : DEFAULT_FOLLOW_UP_MAX_DAYS;
    if (max <= min) max = min + 1;
    return { min, max };
}

// Computes the macro state for a whole row, plus the worst days-outstanding count
// across all five status cells. Used for the row stripe, header badge, sort + filters.
function rowMacroState(cells, range) {
    const { min, max } = resolveRange(range);
    let totalGreen = 0;
    let totalAmber = 0;
    let totalRed = 0;
    let worstDays = 0;
    cells.forEach(({ info }) => {
        if (GREEN_STATUSES.includes(info.status)) totalGreen++;
        else if (AMBER_STATUSES.includes(info.status)) totalAmber++;
        else totalRed++;
        if (!GREEN_STATUSES.includes(info.status)) {
            const days = daysFromSince(info.since);
            if (typeof days === 'number' && days > worstDays) worstDays = days;
        }
    });
    let state;
    if (totalRed === 0 && totalAmber === 0) state = 'complete';
    else if (worstDays >= max) state = 'stalled';
    else if (worstDays >= min) state = 'action_required';
    else state = 'in_progress';
    return { state, totalGreen, totalAmber, totalRed, worstDays, total: cells.length, min, max };
}

const MACRO_PALETTE = {
    complete:        { stripe: '#10B981', bg: '#ECFDF5', fg: '#065F46', border: '#A7F3D0', label: 'Done' },
    in_progress:     { stripe: '#3B82F6', bg: '#EFF6FF', fg: '#1D4ED8', border: '#BFDBFE', label: 'Active' },
    action_required: { stripe: '#F59E0B', bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D', label: 'Follow up' },
    stalled:         { stripe: '#EF4444', bg: '#FEF2F2', fg: '#991B1B', border: '#FECACA', label: 'Stalled' }
};

// Public so the parent page can sort + filter rows without recomputing locally
export function rowMacroStateForRow(row, range) {
    const applicants = Object.values(row.applicants || {});
    const ff = row.fact_find || { status: 'incomplete', since: null };
    const id = summariseInfo(applicants.map((a) => a.id_verification || {}), ['flagged', 'pending', 'passed'], 'id_verification');
    const doc = summariseInfo(applicants.map((a) => a.documents || {}), ['pending', 'partial', 'complete'], 'documents');
    const bank = summariseInfo(applicants.map((a) => a.bank_statements || {}), ['pending', 'received'], 'bank_statements');
    const priv = summariseInfo(applicants.map((a) => a.privacy_consent || {}), ['pending', 'expired', 'valid'], 'privacy_consent');
    return rowMacroState(
        [{ info: ff }, { info: id }, { info: doc }, { info: bank }, { info: priv }],
        range
    );
}

function daysFromSince(since) {
    if (!since) return null;
    const d = new Date(since);
    if (isNaN(d.getTime())) return null;
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    return diff < 0 ? 0 : diff;
}

function pluralDays(n) {
    return n === 1 ? '1 day' : `${n} days`;
}

// Returns the human-readable activity line shown beneath each status pill.
// Uses different verbs depending on status, field, and (for bank stmts) Frollo vs upload.
function activityLine(field, info) {
    const days = daysFromSince(info.since);
    const status = info.status;
    if (days === null) return '-';

    if (GREEN_STATUSES.includes(status)) {
        let verb = field.greenVerb;
        if (field.key === 'bank_statements' && info.via === 'frollo') verb = 'Frollo received';
        if (days === 0) return `${verb} today`;
        if (days === 1) return `${verb} yesterday`;
        return `${verb} ${days}d ago`;
    }
    if (status === 'partial') {
        if (days === 0) return 'Requested today';
        if (days === 1) return 'Requested 1 day ago';
        return `Requested ${days}d ago, partial`;
    }
    if (status === 'expired') {
        return `Expired ${days === 0 ? 'today' : days + 'd ago'}`;
    }
    if (status === 'flagged') {
        if (days === 0) return 'Flagged today';
        return `Flagged ${days}d ago`;
    }
    if (days === 0) return 'Awaiting · today';
    return `${pluralDays(days)} outstanding`;
}

// Aggregates per-borrower statuses up to a single row-level status.
// Worst-case wins so a row is only "green" when every borrower is green.
function summariseStatuses(items, order) {
    const seen = items.map((i) => i.status);
    for (const candidate of order) {
        if (seen.includes(candidate)) return candidate;
    }
    return seen[0] || 'pending';
}

function summariseInfo(items, order, fieldKey) {
    if (!items.length) return { status: 'pending', since: null };
    const status = summariseStatuses(items, order);
    // For days line, surface the WORST (most outstanding / most recent change)
    let since = null;
    let viaFrollo = false;
    items.forEach((i) => {
        if (!i.since) return;
        const d = new Date(i.since).getTime();
        if (since === null || (GREEN_STATUSES.includes(status) ? d > since : d < since)) {
            since = d;
        }
        if (fieldKey === 'bank_statements' && i.via === 'frollo') viaFrollo = true;
    });
    return {
        status,
        since: since ? new Date(since).toISOString() : null,
        via: viaFrollo ? 'frollo' : null
    };
}

function fmtDate(value) {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    const days = daysFromSince(value);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default class TrackingRow extends Component {
    constructor(props) {
        super(props);
        this.state = { expanded: false };
    }

    toggle = () => this.setState((s) => ({ expanded: !s.expanded }));

    rowSummary = () => {
        const { row } = this.props;
        const applicants = Object.values(row.applicants || {});
        const ff = row.fact_find || { status: 'incomplete', since: null };
        const id = summariseInfo(applicants.map((a) => a.id_verification || {}), ['flagged', 'pending', 'passed'], 'id_verification');
        const doc = summariseInfo(applicants.map((a) => a.documents || {}), ['pending', 'partial', 'complete'], 'documents');
        const bank = summariseInfo(applicants.map((a) => a.bank_statements || {}), ['pending', 'received'], 'bank_statements');
        const priv = summariseInfo(applicants.map((a) => a.privacy_consent || {}), ['pending', 'expired', 'valid'], 'privacy_consent');
        return [
            { field: FIELDS[0], info: ff },
            { field: FIELDS[1], info: id },
            { field: FIELDS[2], info: doc },
            { field: FIELDS[3], info: bank },
            { field: FIELDS[4], info: priv }
        ];
    };

    renderStatusCell = ({ field, info, compact }) => {
        return (
            <div key={field.key} className="flex flex-col gap-1">
                <span
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: '#94A3B8' }}
                >
                    {field.label}
                </span>
                <StatusPill status={info.status} compact={compact} />
                <span className="text-[11px]" style={{ color: '#64748B' }}>
                    {activityLine(field, info)}
                </span>
            </div>
        );
    };

    renderApplicantTable = () => {
        const applicants = Object.values(this.props.row.applicants || {});
        if (!applicants.length) {
            return (
                <div className="text-sm" style={{ color: '#64748B' }}>No applicants on this application.</div>
            );
        }
        return (
            <div className="overflow-hidden rounded-lg" style={{ border: '1px solid #E2E8F0' }}>
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#F8FAFC' }}>
                            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#64748B' }}>Borrower</th>
                            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#64748B' }}>ID Verification</th>
                            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#64748B' }}>Documents</th>
                            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#64748B' }}>Bank Statements</th>
                            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#64748B' }}>Privacy</th>
                        </tr>
                    </thead>
                    <tbody>
                        {applicants.map((a, idx) => (
                            <tr
                                key={a.id}
                                style={{
                                    borderTop: idx === 0 ? 'none' : '1px solid #E2E8F0',
                                    backgroundColor: '#FFFFFF'
                                }}
                            >
                                <td className="px-4 py-3 align-top">
                                    <div className="font-semibold" style={{ color: '#0F172A' }}>{a.name || '-'}</div>
                                    <div style={{ color: '#94A3B8', fontSize: 11 }}>{a.email || ''}</div>
                                </td>
                                {[
                                    { field: FIELDS[1], info: a.id_verification || {} },
                                    { field: FIELDS[2], info: a.documents || {} },
                                    { field: FIELDS[3], info: a.bank_statements || {} },
                                    { field: FIELDS[4], info: a.privacy_consent || {} }
                                ].map(({ field, info }) => (
                                    <td key={field.key} className="px-4 py-3 align-top">
                                        <StatusPill
                                            status={info.status}
                                            compact
                                            label={field.key === 'bank_statements' && info.status === 'received' && info.via === 'frollo' ? 'Received · Frollo' : undefined}
                                        />
                                        <div className="mt-1 text-[11px]" style={{ color: '#64748B' }}>
                                            {activityLine(field, info)}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    render() {
        const { row, onArchive, onUnarchive, onOpenApplication } = this.props;
        const applicants = Object.values(row.applicants || {});
        const applicantNames = applicants.map((a) => a.name).filter(Boolean).join(' & ');
        const isArchived = row.tracking_status === 'archived';
        const summaryCells = this.rowSummary();
        const macro = rowMacroState(summaryCells, this.props.range);
        const palette = MACRO_PALETTE[macro.state];
        const stripeColour = isArchived ? '#94A3B8' : palette.stripe;

        return (
            <div
                className="rounded-xl mb-3 transition-shadow overflow-hidden"
                style={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    boxShadow: isArchived ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.04)',
                    opacity: isArchived ? 0.78 : 1,
                    position: 'relative'
                }}
            >
                <span
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        backgroundColor: stripeColour
                    }}
                />
                <div className="pl-6 pr-5 pt-4 pb-5">
                    <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <button
                                    type="button"
                                    onClick={this.toggle}
                                    className="rounded-md w-6 h-6 flex items-center justify-center shrink-0"
                                    style={{
                                        backgroundColor: this.state.expanded ? '#EFF6FF' : '#F1F5F9',
                                        color: this.state.expanded ? '#1D4ED8' : '#475569',
                                        border: `1px solid ${this.state.expanded ? '#BFDBFE' : '#E2E8F0'}`,
                                        cursor: 'pointer'
                                    }}
                                    aria-label={this.state.expanded ? 'Collapse' : 'Expand'}
                                >
                                    <span style={{ fontSize: 11, lineHeight: 1 }}>
                                        {this.state.expanded ? '▾' : '▸'}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={onOpenApplication}
                                    className="text-base font-bold truncate hover:underline"
                                    style={{ color: '#0F172A', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                >
                                    {applicantNames || row.name || row.application_id}
                                </button>
                                {isArchived ? (
                                    <span
                                        className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 font-bold shrink-0"
                                        style={{ backgroundColor: '#E2E8F0', color: '#475569' }}
                                    >
                                        Archived{row.archived_reason ? ` · ${row.archived_reason.replace('_', ' ')}` : ''}
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider rounded-full px-2.5 py-0.5 font-bold shrink-0"
                                        style={{
                                            backgroundColor: palette.bg,
                                            color: palette.fg,
                                            border: `1px solid ${palette.border}`
                                        }}
                                    >
                                        <span
                                            className="inline-block rounded-full"
                                            style={{ width: 6, height: 6, backgroundColor: palette.stripe }}
                                        />
                                        {palette.label}
                                        {(macro.state === 'action_required' || macro.state === 'stalled') && macro.worstDays > 0 ? (
                                            <span style={{ opacity: 0.85 }}>· {macro.worstDays}d</span>
                                        ) : null}
                                    </span>
                                )}
                                <span
                                    className="inline-flex items-center gap-2 text-[11px] font-semibold shrink-0"
                                    style={{ color: '#475569' }}
                                >
                                    <span
                                        aria-hidden
                                        className="inline-block rounded-full overflow-hidden"
                                        style={{ width: 80, height: 6, backgroundColor: '#E2E8F0' }}
                                    >
                                        <span
                                            className="block h-full"
                                            style={{
                                                width: `${Math.round((macro.totalGreen / macro.total) * 100)}%`,
                                                backgroundColor: palette.stripe,
                                                transition: 'width 0.3s ease'
                                            }}
                                        />
                                    </span>
                                    <span style={{ color: '#64748B' }}>
                                        {macro.totalGreen}/{macro.total} done
                                    </span>
                                </span>
                            </div>
                            <div className="text-xs" style={{ color: '#94A3B8' }}>
                                {applicants.length} {applicants.length === 1 ? 'borrower' : 'borrowers'}
                                {row.last_activity ? ` · last activity ${fmtDate(row.last_activity)}` : ''}
                                {row.created ? ` · created ${fmtDate(row.created)}` : ''}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isArchived ? (
                                <button
                                    type="button"
                                    onClick={onUnarchive}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-md"
                                    style={{ backgroundColor: '#10B981', color: '#FFFFFF' }}
                                >
                                    Reactivate
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onArchive}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-md"
                                    style={{ backgroundColor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0' }}
                                >
                                    Archive
                                </button>
                            )}
                        </div>
                    </div>

                    <div
                        className="grid gap-4"
                        style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
                    >
                        {summaryCells.map((cell) => this.renderStatusCell({ ...cell, compact: false }))}
                    </div>
                </div>

                {this.state.expanded ? (
                    <div className="pl-6 pr-5 pb-5">
                        {this.renderApplicantTable()}
                    </div>
                ) : null}
            </div>
        );
    }
}
